import { DatePicker, Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import qs from 'qs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getNotices } from '../../../api/boardNotice.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import CampusDetailModal from '../../../components/modals/CampusDetailModal';
import GroupDetailModal from '../../../components/modals/GroupDetailModal';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { IGetNotices, INoticeItem } from '../../../models/boardNotice.model';
import { RootState } from '../../../store/rootReducer';
import SelectCampus from '../../../components/SelectCampus';

const BoardNotice = () => {
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { t, i18n } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { groups } = useSelector((state: RootState) => state.group);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0004 = 지역코드
  const SY0004 = commonCodes.find((item) => item.commonCode === 'SY0004')?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetNotices>();

  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [campusCode, setCampusCode] = useState<string>();
  const [groupCode, setGroupCode] = useState<number>();
  const [srtDate, setSrtDate] = useState(moment().subtract(1, 'months').format('YYYYMMDD'));
  const [endDate, setEndDate] = useState(moment().format('YYYYMMDD'));
  const [field, setField] = useState('title');
  const [keyword, setKeyword] = useState('');
  const [noticeCode, setNoticeCode] = useState<number>();
  const [campusDetailVisible, setCampusDetailVisible] = useState(false);
  const [groupDetailVisible, setGroupDetailVisible] = useState(false);

  useEffect(() => {
    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(_param.pageNo);
    }
    if (query.campusCode) {
      _param.campusCode = String(query.campusCode);
      setCampusCode(_param.campusCode);
    }
    if (query.groupCode) {
      _param.groupCode = Number(query.groupCode);
      setGroupCode(_param.groupCode);
    }
    if (query.srtDate && query.endDate) {
      _param.srtDate = String(query.srtDate);
      _param.endDate = String(query.endDate);
      setSrtDate(_param.srtDate);
      setEndDate(_param.endDate);
    } else if (srtDate && endDate) {
      _param.srtDate = srtDate;
      _param.endDate = endDate;
    }
    if (query.field && query.keyword) {
      const _field = String(query.field);
      const _keyword = String(query.keyword);
      setField(_field);
      setKeyword(_keyword);
      if (field === 'title') _param.title = _keyword;
      else if (i18n.language === 'ko') _param.regMemKorName = _keyword;
      else _param.regMemEngName = _keyword;
    }

    execute(getNotices(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  }, [location]);

  const getCampusInfo = (campusCode: string, type: string) => {
    const campus = campusData?.find(
      (item: any) => item.campusCode === campusCode,
    );

    switch (type) {
      case 'name':
        return i18n.language === 'ko' ? campus?.campusKorName : campus?.campusEngName;
      case 'region': {
        const region = SY0004?.find(
          (item: any) => item.detailCode === campus?.sidoCode,
        );
        return i18n.language === 'ko' ? region?.korName : region?.engName;
      }
    }
  };

  const getGroupName = (id: number) => {
    const group = groups?.find((item: any) => item.groupNo === Number(id));
    return group?.korName;
  };

  const onSearch = () => {
    const param: any = { page: 1 };

    if (campusCode) param.campusCode = campusCode;
    if (groupCode) param.groupCode = groupCode;
    if (srtDate && endDate) {
      param.srtDate = srtDate;
      param.endDate = endDate;
    }
    if (keyword) {
      param.field = field;
      param.keyword = keyword;
    }

    history.push(`/board/notice?${qs.stringify(param)}`);
  };

  const columns: TableColumnsType<INoticeItem> = [
    {
      title: 'No.',
      dataIndex: 'noticeCode',
      key: 'noticeCode',
      width: '5%',
      align: 'center',
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('common.menu.campus'),
      dataIndex: 'allCampusYn',
      key: 'allCampusYn',
      width: '10%',
      align: 'center',
      render: (value, record: any) => {
        let render;

        if (record.allCampusYn === 'Y') {
          render = t('common.text.all');
        } else if (record.campusList?.length === 1) {
          render = getCampusInfo(record.campusList[0], 'name');
        } else {
          render = (
            // eslint-disable-next-line
            <a
              className="download-link"
              onClickCapture={(event) => {
                event.stopPropagation();
                setNoticeCode(record.noticeCode);
                setCampusDetailVisible(true);
              }}
            >
              {t('board.notice.option1')}
            </a>
          );
        }

        return render;
      },
    },
    {
      title: t('board.notice.column1'),
      dataIndex: 'allGroupYn',
      key: 'allGroupYn',
      width: '10%',
      align: 'center',
      render: (value, record: any) => {
        let render;

        if (record.allGroupYn === 'Y') {
          render = t('common.text.all');
        } else if (record.groupList?.length === 1) {
          return getGroupName(record.groupList[0]);
        } else {
          render = (
            // eslint-disable-next-line
            <a
              className="download-link"
              onClickCapture={(event) => {
                event.stopPropagation();
                setNoticeCode(record.noticeCode);
                setGroupDetailVisible(true);
              }}
            >
              {t('board.notice.option1')}
            </a>
          );
        }

        return render;
      },
    },
    {
      title: t('board.notice.1'),
      dataIndex: 'regGroKorName',
      key: 'regGroKorName',
      width: '10%',
      align: 'center',
      render: (value, record) => i18n.language === 'ko' ? value : record.regGroEngName,
    },
    {
      title: t('board.notice.2'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      width: '10%',
      align: 'center',
    },
    {
      title: t('archive.learn.column2'),
      dataIndex: 'regDate',
      key: 'regDate',
      width: '10%',
      align: 'center',
      render: (regDate: string) => moment(regDate).format('YYYY.MM.DD'),
    },
    {
      title: t('archive.learn.column3'),
      dataIndex: 'readCount',
      key: 'readCount',
      width: '10%',
      align: 'center',
    },
    {
      title: t('board.notice.column2'),
      dataIndex: 'openYn',
      key: 'openYn',
      width: '10%',
      align: 'center',
    },
  ];

  const columns2: TableColumnsType<INoticeItem> = [
    {
      title: 'No.',
      dataIndex: 'noticeCode',
      key: 'noticeCode',
      width: '5%',
      align: 'center',
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('archive.learn.column1'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      width: '15%',
      align: 'center',
      render: (value, record) =>
        `${record.regGroKorName} (${record.regMemKorName})`,
    },
    {
      title: t('archive.learn.column2'),
      dataIndex: 'regDate',
      key: 'regDate',
      width: '10%',
      align: 'center',
      render: (regDate: string) => moment(regDate).format('YYYY.MM.DD'),
    },
    {
      title: t('archive.learn.column3'),
      dataIndex: 'readCount',
      key: 'readCount',
      width: '10%',
      align: 'center',
    },
  ];
  useEffect (()=> {
    console.log(campusCode)
  }, [campusCode]);
  return (
    <>
    <SimpleHeader
      iconFile="icon-board-big.svg"
      parentMenu={t('common.menu.board')}
      currentMenu={t('board.notice.title1')}
      onlyTitle={true}
    />	
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus
                placeholder={t('common.menu.campus')}
                value={campusCode}
                onChange={(value: string) => setCampusCode(value)}
                hasAll={true}
              />
            </div>
          </li>
          <li>
            <label>{t('board.notice.column1')}</label>
            <div>
              <Select
                placeholder={t('board.notice.column1')}
                value={groupCode}
                onChange={(value) => setGroupCode(value)}
              >
                <Select.Option value="">{t('common.text.all')}</Select.Option>
                {groups?.map((item) => {
                  return (
                    <Select.Option key={item.groupNo} value={item.groupNo}>
                      {i18n.language === 'ko' ? item.korName : item.engName}
                    </Select.Option>
                  );
                })}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('archive.learn.column2')}</label>
            <div className="date-picker__area">
              <DatePicker
                value={srtDate ? moment(srtDate) : undefined}
                onChange={(e) => {
                  if (e) {
                    setSrtDate(moment(e).format('YYYYMMDD'));
                  } 
                }}
              />
            </div>
            ~
            <div className="date-picker__area">
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={(e) => {
                  if (e) {
                    setEndDate(moment(e).format('YYYYMMDD'));
                  }
                }}
              />
            </div>
          </li>
          <li>
            <div>
              <Select
                value={field}
                onChange={(value) => setField(value)}
              >
                <Select.Option value="title">{t('common.column.title')}</Select.Option>
                <Select.Option value="author">{t('board.notice.2')}</Select.Option>
              </Select>
            </div>
            <div>
              <input
                type="text"
                placeholder={t('board.notice.placeholder2')}
                className="form-control"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      
      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">{t('common.text.count')}: <b>{res?.total}</b>{t('common.text.unit')}</div>
          <div className="comm-btn__area">
            {auth?.hqYn === 'Y' ? (
              <Button
                className="btn-basic pink"
                onClick={() =>
                  history.push(`/board/notice/create?${qs.stringify(param)}`)
                }
                text={t('archive.learn.button1')}
                writeAuthYn={writeAuthYn}
              />
            ) : null}
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={auth?.hqYn === 'Y' ? columns : columns2 }
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/board/notice/${record.noticeCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="noticeCode"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/board/notice"
            />
          </div>
          <div className="comm-btn__area right">
            {auth?.hqYn === 'Y' ? (
              <Button
                className="btn-basic pink"
                onClick={() =>
                  history.push(`/board/notice/create?${qs.stringify(param)}`)
                }
                text={t('archive.learn.button1')}
                writeAuthYn={writeAuthYn}
              />
            ) : null}
          </div>
        </div>
      </div>

      {campusDetailVisible &&
        <CampusDetailModal
          campusDetailVisible={campusDetailVisible}
          setCampusDetailVisible={setCampusDetailVisible}
          getCampusInfo={getCampusInfo}
          noticeCode={noticeCode}
        />
      }
      {groupDetailVisible &&
        <GroupDetailModal
          groupDetailVisible={groupDetailVisible}
          setGroupDetailVisible={setGroupDetailVisible}
          getGroupName={getGroupName}
          noticeCode={noticeCode}
        />      
      }
    </div>
    </>
  );
};

export default BoardNotice;
